import { useCallback, useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { useQuery, useSubscription, useMutation } from "urql"
import { RefreshCw, Pencil, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"

import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { CopyButton } from "@/components/shared/copy-button"
import { JsonViewer } from "@/components/shared/json-viewer"
import { DataTable, type Column } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { GlassCard } from "@/components/shared/glass-card"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { EndpointForm } from "../components/endpoint-form"
import { Badge } from "@/components/ui/badge"
import { EndpointQuery, OutboundDeliveriesQuery, OutboundDeliveryUpdatedSubscription, RotateEndpointSecretMutation, RegenerateOutboundPollApiKeyMutation, PollOutboundDeliveriesQuery, AckOutboundDeliveriesMutation } from "@/lib/graphql/outbound"
import { Checkbox } from "@/components/ui/checkbox"
import { useAuth } from "@/hooks/use-auth"
import { destinationTypeLabels, type DestinationTypeValue } from "@/lib/schemas"
import type { ResultOf } from "@/lib/graphql/types"

type DeliveryItem = ResultOf<typeof OutboundDeliveriesQuery>["outboundDeliveries"]["nodes"][number]
type PollDeliveryItem = ResultOf<typeof PollOutboundDeliveriesQuery>["pollOutboundDeliveries"]["nodes"][number]

const deliveryColumns: Column<DeliveryItem>[] = [
  {
    header: "Event Type",
    cell: (row) => <code className="text-xs font-mono">{row.message.eventType}</code>,
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Attempts",
    cell: (row) => (
      <span className="text-xs tabular-nums">{row.attempts}/{row.maxAttempts}</span>
    ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

export function EndpointDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isConfigured } = useAuth()
  const [result, reexecute] = useQuery({ query: EndpointQuery, variables: { id: id! } })
  const [, rotateSecret] = useMutation(RotateEndpointSecretMutation)
  const [, regeneratePollKey] = useMutation(RegenerateOutboundPollApiKeyMutation)
  const [, ackDeliveries] = useMutation(AckOutboundDeliveriesMutation)
  const [editOpen, setEditOpen] = useState(false)
  const [rotateOpen, setRotateOpen] = useState(false)
  const [rotating, setRotating] = useState(false)
  const [secretVisible, setSecretVisible] = useState(false)
  const [regenPollKeyOpen, setRegenPollKeyOpen] = useState(false)
  const [regeneratingPollKey, setRegeneratingPollKey] = useState(false)
  const [pollKeyVisible, setPollKeyVisible] = useState(false)
  const [selectedPollIds, setSelectedPollIds] = useState<Set<string>>(new Set())
  const [acking, setAcking] = useState(false)
  const [pollCursor, setPollCursor] = useState<string | undefined>(undefined)
  const isPoll = result.data?.endpoint?.deliveryMode === "POLL"

  const [deliveries, reexecuteDeliveries] = useQuery({
    query: OutboundDeliveriesQuery,
    variables: { endpointId: id, limit: 10, offset: 0 },
    pause: isPoll,
  })

  const [pollResult, reexecutePoll] = useQuery({
    query: PollOutboundDeliveriesQuery,
    variables: { endpointId: id!, cursor: pollCursor, limit: 25 },
    pause: !isPoll,
  })

  const togglePollId = useCallback((deliveryId: string) => {
    setSelectedPollIds((prev) => {
      const next = new Set(prev)
      if (next.has(deliveryId)) next.delete(deliveryId)
      else next.add(deliveryId)
      return next
    })
  }, [])

  const handleAck = async () => {
    if (selectedPollIds.size === 0) return
    setAcking(true)
    const res = await ackDeliveries({ endpointId: id!, deliveryIds: Array.from(selectedPollIds) })
    setAcking(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success(`Acknowledged ${res.data?.ackOutboundDeliveries ?? selectedPollIds.size} deliveries`)
    setSelectedPollIds(new Set())
    reexecutePoll({ requestPolicy: "network-only" })
  }

  const handleDeliveryUpdated = useCallback(
    (
      _prev: ResultOf<typeof OutboundDeliveryUpdatedSubscription> | undefined,
      response: ResultOf<typeof OutboundDeliveryUpdatedSubscription>,
    ) => {
      if (response?.outboundDeliveryUpdated) {
        reexecuteDeliveries({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecuteDeliveries],
  )

  useSubscription(
    { query: OutboundDeliveryUpdatedSubscription, variables: { endpointId: id }, pause: !isConfigured },
    handleDeliveryUpdated,
  )

  const ep = result.data?.endpoint

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!ep) return <EmptyState title="Endpoint not found" />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">{ep.deliveryMode === "POLL" ? `Poll Endpoint` : ep.url}</h1>
          <StatusBadge status={ep.status} />
          <Badge variant={ep.type === "HTTP" ? "secondary" : "outline"} className="text-2xs">
            {destinationTypeLabels[ep.type as DestinationTypeValue] ?? ep.type}
          </Badge>
          {ep.type === "HTTP" && (
            <Badge variant={ep.deliveryMode === "POLL" ? "outline" : "default"} className="text-2xs">
              {ep.deliveryMode}
            </Badge>
          )}
          {ep.ordered && (
            <Badge variant="outline" className="text-2xs">Ordered</Badge>
          )}
          {ep.disabledReason && (
            <Badge variant="destructive" className="text-2xs">
              {ep.disabledReason === "unhealthy" ? "Auto-disabled: unhealthy" : `Disabled: ${ep.disabledReason}`}
            </Badge>
          )}
          {(() => {
            const score = ep.healthScore
            const color = score >= 0.8 ? "bg-emerald-500" : score >= 0.4 ? "bg-amber-500" : "bg-red-500"
            return (
              <div className="flex items-center gap-1.5">
                <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
                <span className="text-xs tabular-nums">{Math.round(score * 100)}%</span>
              </div>
            )
          })()}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Edit
          </Button>
        </div>
      </div>

      {ep.ordered && ep.blockedDelivery && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium">Ordered delivery is blocked</p>
            <p className="text-xs text-muted-foreground">
              Delivery{" "}
              <Link
                to={`/outbound-deliveries/${ep.blockedDelivery.id}`}
                className="font-mono hover:underline text-foreground"
              >
                {ep.blockedDelivery.id.slice(0, 8)}
              </Link>
              {" "}for event type{" "}
              <code className="font-mono">{ep.blockedDelivery.message.eventType}</code>
              {" "}has failed after {ep.blockedDelivery.attempts}/{ep.blockedDelivery.maxAttempts} attempts.
              Subsequent deliveries are paused. Replay or skip the DLQ entry to unblock.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <FieldLabel className="mb-1">Type</FieldLabel>
                <Badge variant={ep.type === "HTTP" ? "secondary" : "outline"} className="text-2xs">
                  {destinationTypeLabels[ep.type as DestinationTypeValue] ?? ep.type}
                </Badge>
              </div>
              {ep.type === "HTTP" && (
                <div>
                  <FieldLabel className="mb-1">Delivery Mode</FieldLabel>
                  <Badge variant={ep.deliveryMode === "POLL" ? "outline" : "secondary"} className="text-2xs">
                    {ep.deliveryMode}
                  </Badge>
                </div>
              )}
              {ep.type === "HTTP" && ep.deliveryMode !== "POLL" && (
                <div>
                  <FieldLabel className="mb-1">URL</FieldLabel>
                  <div className="flex items-center gap-1">
                    <code className="font-mono text-xs truncate">{ep.url}</code>
                    <CopyButton text={ep.url} />
                  </div>
                </div>
              )}
              <div>
                <FieldLabel className="mb-1">Application</FieldLabel>
                <Link to={`/applications/${ep.application.id}`} className="text-sm hover:underline">
                  {ep.application.name}
                </Link>
              </div>
              {ep.type === "HTTP" && ep.deliveryMode !== "POLL" && (
                <div>
                  <FieldLabel className="mb-1">Timeout</FieldLabel>
                  <div className="tabular-nums">{ep.timeoutMs}ms</div>
                </div>
              )}
              <div>
                <FieldLabel className="mb-1">Rate Limit</FieldLabel>
                <div>{ep.rateLimitRps > 0 ? `${ep.rateLimitRps} rps` : "Unlimited"}</div>
              </div>
              {ep.authType && ep.authType !== "NONE" && (
                <div>
                  <FieldLabel className="mb-1">Auth Type</FieldLabel>
                  <Badge variant="outline" className="text-2xs">{ep.authType}</Badge>
                </div>
              )}
              {ep.outputFormat && (
                <div>
                  <FieldLabel className="mb-1">Output Format</FieldLabel>
                  <div className="font-mono text-xs">{ep.outputFormat}</div>
                </div>
              )}
              {ep.connectorType && (
                <div>
                  <FieldLabel className="mb-1">Connector</FieldLabel>
                  <div className="font-mono text-xs">{ep.connectorType}</div>
                </div>
              )}
              {ep.autoDeletePayload && (
                <div>
                  <FieldLabel className="mb-1">Auto-Delete Payload</FieldLabel>
                  <Badge variant="default" className="text-2xs">Enabled</Badge>
                </div>
              )}
              {ep.delay && (
                <div>
                  <FieldLabel className="mb-1">Delay</FieldLabel>
                  <div className="font-mono text-xs">{ep.delay}</div>
                </div>
              )}
              {ep.channels && ep.channels.length > 0 && (
                <div>
                  <FieldLabel className="mb-1">Channels</FieldLabel>
                  <div className="flex flex-wrap gap-1">
                    {ep.channels.map((ch: string) => (
                      <Badge key={ch} variant="outline" className="text-2xs font-mono">{ch}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {ep.authType === "OAUTH2" && ep.oauth2Config && (
              <div>
                <FieldLabel className="mb-2">OAuth2 Config</FieldLabel>
                <GlassCard className="p-3">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-muted-foreground">Token URL</div>
                      <div className="font-medium font-mono truncate">{ep.oauth2Config.tokenUrl}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Client ID</div>
                      <div className="font-medium font-mono truncate">{ep.oauth2Config.clientId}</div>
                    </div>
                    {ep.oauth2Config.scopes && (
                      <div>
                        <div className="text-muted-foreground">Scopes</div>
                        <div className="font-medium font-mono">{ep.oauth2Config.scopes}</div>
                      </div>
                    )}
                    {ep.oauth2Config.audience && (
                      <div>
                        <div className="text-muted-foreground">Audience</div>
                        <div className="font-medium font-mono truncate">{ep.oauth2Config.audience}</div>
                      </div>
                    )}
                  </div>
                </GlassCard>
              </div>
            )}
            {ep.authType === "MTLS" && (ep.mtlsCert || ep.mtlsKey) && (
              <div>
                <FieldLabel className="mb-2">mTLS Certificates</FieldLabel>
                <GlassCard className="p-3 space-y-2">
                  {ep.mtlsCert && (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Client Certificate</div>
                      <code className="text-2xs font-mono block truncate">{ep.mtlsCert.slice(0, 60)}...</code>
                    </div>
                  )}
                  {ep.mtlsKey && (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Client Key</div>
                      <code className="text-2xs font-mono block truncate">{ep.mtlsKey.slice(0, 60)}...</code>
                    </div>
                  )}
                </GlassCard>
              </div>
            )}
            {ep.type !== "HTTP" && ep.typeConfig && (
              <div>
                <FieldLabel className="mb-2">{destinationTypeLabels[ep.type as DestinationTypeValue]} Config</FieldLabel>
                <JsonViewer data={ep.typeConfig} collapsed />
              </div>
            )}
            {ep.filterConfig && ep.filterConfig.eventTypes && ep.filterConfig.eventTypes.length > 0 && (
              <div>
                <FieldLabel className="mb-2">Event Filter</FieldLabel>
                <div className="flex flex-wrap gap-1.5">
                  {ep.filterConfig.eventTypes.map((et) => (
                    <code key={et} className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">{et}</code>
                  ))}
                </div>
              </div>
            )}
            {ep.retryPolicy && (
              <div>
                <FieldLabel className="mb-2">Retry Policy</FieldLabel>
                <GlassCard className="p-3">
                  <div className="grid grid-cols-4 gap-3 text-xs">
                    <div>
                      <div className="text-muted-foreground">Max Attempts</div>
                      <div className="font-medium tabular-nums">{ep.retryPolicy.maxAttempts}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Initial Delay</div>
                      <div className="font-medium font-mono">{ep.retryPolicy.initialDelay}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Max Delay</div>
                      <div className="font-medium font-mono">{ep.retryPolicy.maxDelay}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Backoff</div>
                      <div className="font-medium tabular-nums">{ep.retryPolicy.backoffFactor}x</div>
                    </div>
                  </div>
                </GlassCard>
              </div>
            )}
            {ep.headers && (
              <div>
                <FieldLabel className="mb-2">Custom Headers</FieldLabel>
                <JsonViewer data={ep.headers} collapsed />
              </div>
            )}
            {ep.healthConfig && (
              <div>
                <FieldLabel className="mb-2">Health Monitoring</FieldLabel>
                <GlassCard className="p-3">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-muted-foreground">Window</div>
                      <div className="font-medium tabular-nums">{ep.healthConfig.windowHours}h</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Disable Below</div>
                      <div className="font-medium tabular-nums">{Math.round(ep.healthConfig.disableBelow * 100)}%</div>
                    </div>
                  </div>
                </GlassCard>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <FieldLabel className="mb-1">ID</FieldLabel>
              <div className="flex items-center gap-1">
                <code className="font-mono text-xs">{ep.id}</code>
                <CopyButton text={ep.id} />
              </div>
            </div>
            <div>
              <FieldLabel className="mb-1">Signing Secret</FieldLabel>
              <div className="flex items-center gap-1">
                <code className="font-mono text-xs">{"whsec_" + "•".repeat(20)}</code>
                <button
                  type="button"
                  className="text-2xs text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setSecretVisible(true)}
                >
                  Show
                </button>
                <CopyButton text={ep.signingSecret} />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setRotateOpen(true)}
              >
                <RefreshCw className="h-3 w-3 mr-1.5" />
                Rotate
              </Button>
            </div>
            {ep.deliveryMode === "POLL" && (
              <div>
                <FieldLabel className="mb-1">Poll API Key</FieldLabel>
                <div className="flex items-center gap-1">
                  <code className="font-mono text-xs">
                    {ep.pollApiKeyPrefix ? `${ep.pollApiKeyPrefix}${"•".repeat(16)}` : "Not generated"}
                  </code>
                  {ep.pollApiKey && (
                    <>
                      <button
                        type="button"
                        className="text-2xs text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setPollKeyVisible(true)}
                      >
                        Show
                      </button>
                      <CopyButton text={ep.pollApiKey} />
                    </>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setRegenPollKeyOpen(true)}
                >
                  <RefreshCw className="h-3 w-3 mr-1.5" />
                  Regenerate
                </Button>
              </div>
            )}
            <div>
              <FieldLabel className="mb-1">Created</FieldLabel>
              <TimeAgo date={ep.createdAt} />
            </div>
          </CardContent>
        </Card>
      </div>

      {ep.metrics && (
        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Performance (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-xs text-muted-foreground">P50</div>
                <div className="text-lg font-semibold tabular-nums">{ep.metrics.p50}ms</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-muted-foreground">P95</div>
                <div className="text-lg font-semibold tabular-nums">{ep.metrics.p95}ms</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-muted-foreground">P99</div>
                <div className="text-lg font-semibold tabular-nums">{ep.metrics.p99}ms</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-muted-foreground">Max</div>
                <div className="text-lg font-semibold tabular-nums">{ep.metrics.max}ms</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {ep.deliveryMode === "POLL" ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">Poll Queue</h2>
            <div className="flex items-center gap-2">
              {selectedPollIds.size > 0 && (
                <Button
                  size="sm"
                  onClick={handleAck}
                  disabled={acking}
                >
                  {acking ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Acknowledge ({selectedPollIds.size})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => reexecutePoll({ requestPolicy: "network-only" })}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Refresh
              </Button>
            </div>
          </div>
          <DataTable<PollDeliveryItem>
            columns={[
              {
                header: "",
                cell: (row) => (
                  <Checkbox
                    checked={selectedPollIds.has(row.id)}
                    onCheckedChange={() => togglePollId(row.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                ),
                className: "w-10",
              },
              {
                header: "Event Type",
                cell: (row) => <code className="text-xs font-mono">{row.message.eventType}</code>,
              },
              {
                header: "Status",
                cell: (row) => <StatusBadge status={row.status} />,
              },
              {
                header: "Attempts",
                cell: (row) => (
                  <span className="text-xs tabular-nums">{row.attempts}/{row.maxAttempts}</span>
                ),
              },
              {
                header: "Created",
                cell: (row) => <TimeAgo date={row.createdAt} />,
              },
            ]}
            data={pollResult.data?.pollOutboundDeliveries.nodes ?? []}
            keyFn={(row) => row.id}
            loading={pollResult.fetching}
            emptyTitle="No pending deliveries"
            emptyDescription="The poll queue is empty"
            onRowClick={(row) => navigate(`/outbound-deliveries/${row.id}`)}
          />
          {pollResult.data?.pollOutboundDeliveries.pageInfo.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPollCursor(pollResult.data?.pollOutboundDeliveries.pageInfo.endCursor ?? undefined)}
              >
                Load More
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-sm font-medium">Recent Deliveries</h2>
          <DataTable
            columns={deliveryColumns}
            data={deliveries.data?.outboundDeliveries.nodes ?? []}
            keyFn={(row) => row.id}
            loading={deliveries.fetching}
            emptyTitle="No deliveries"
            onRowClick={(row) => navigate(`/outbound-deliveries/${row.id}`)}
          />
        </div>
      )}

      <EndpointForm
        open={editOpen}
        onOpenChange={setEditOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
        initialData={ep}
      />

      <Dialog open={secretVisible} onOpenChange={setSecretVisible}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Signing Secret</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <GlassCard className="p-3">
              <code className="font-mono text-xs break-all select-all">{ep.signingSecret}</code>
            </GlassCard>
            <div className="flex justify-end">
              <CopyButton text={ep.signingSecret} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={rotateOpen}
        onOpenChange={setRotateOpen}
        title="Rotate Signing Secret"
        description="The current secret will become the secondary secret for key rotation. Recipients should verify against both secrets during the transition."
        confirmLabel="Rotate"
        onConfirm={async () => {
          setRotating(true)
          const res = await rotateSecret({ id: id! })
          setRotating(false)
          if (res.error) { toast.error(res.error.message); return }
          toast.success("Signing secret rotated")
          setRotateOpen(false)
          setSecretVisible(true)
          reexecute({ requestPolicy: "network-only" })
        }}
        loading={rotating}
      />

      {ep.deliveryMode === "POLL" && ep.pollApiKey && (
        <Dialog open={pollKeyVisible} onOpenChange={setPollKeyVisible}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Poll API Key</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <GlassCard className="p-3">
                <code className="font-mono text-xs break-all select-all">{ep.pollApiKey}</code>
              </GlassCard>
              <div className="flex justify-end">
                <CopyButton text={ep.pollApiKey} />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog
        open={regenPollKeyOpen}
        onOpenChange={setRegenPollKeyOpen}
        title="Regenerate Poll API Key"
        description="The current poll API key will be invalidated immediately. Any consumers using the current key will lose access."
        confirmLabel="Regenerate"
        onConfirm={async () => {
          setRegeneratingPollKey(true)
          const res = await regeneratePollKey({ endpointId: id! })
          setRegeneratingPollKey(false)
          if (res.error) { toast.error(res.error.message); return }
          toast.success("Poll API key regenerated")
          setRegenPollKeyOpen(false)
          setPollKeyVisible(true)
          reexecute({ requestPolicy: "network-only" })
        }}
        loading={regeneratingPollKey}
      />
    </div>
  )
}
